import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type SdmModuleSpecsFragment } from '../../../../../api/fragments';
import {
    DeleteExternalBankDocument,
    type DeleteExternalBankMutation,
    type DeleteExternalBankMutationVariables,
} from '../../../../../api/mutations/deleteExternalBank';

export type BankIntegrationActionsProps = {
    openBankIntegrationDrawer: (bankIntegrationId?: string) => void;
    bank: SdmModuleSpecsFragment['banks'][number];
    module: SdmModuleSpecsFragment;
};

const BankIntegrationActions = ({ openBankIntegrationDrawer, bank, module }: BankIntegrationActionsProps) => {
    const { t } = useTranslation(['sdmModuleDetails']);
    const apolloClient = useApolloClient();

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('sdmModuleDetails:bankIntegrations.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('sdmModuleDetails:bankIntegrations.deleteModal.content'),
            okText: t('sdmModuleDetails:bankIntegrations.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('sdmModuleDetails:bankIntegrations.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('sdmModuleDetails:bankIntegrations.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteExternalBankMutation, DeleteExternalBankMutationVariables>({
                        mutation: DeleteExternalBankDocument,
                        variables: { moduleId: module.id, bankId: bank.id },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('sdmModuleDetails:bankIntegrations.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, bank.id, module.id, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button onClick={() => openBankIntegrationDrawer(bank.id)} size="small" style={{ padding: 0 }} type="link">
                {t('sdmModuleDetails:actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('sdmModuleDetails:actions.delete')}
            </Button>
        </Space>
    );
};

export default BankIntegrationActions;
